import { useTranslation } from 'react-i18next';
import { useTheme, Typography, Stack, Icon } from '../../../../base';
import {
    IClientSourceOfHireTopStatistics,
    ReportPeriod,
} from '../../../../common';
import {
    StyledCardsRow,
    StyledCardItem,
    StyledIconCircle,
    StyledTrendRow,
} from './SummaryCards.styles';

interface ISummaryCardsProps {
    topStatistics: IClientSourceOfHireTopStatistics;
    timeFrame: string;
}

const TREND_LABEL_KEYS: Record<string, string> = {
    [ReportPeriod.WEEK]: 'vsLastWeek',
    [ReportPeriod.MONTH]: 'vsLastMonth',
    [ReportPeriod.QUARTER]: 'vsLastQuarter',
    [ReportPeriod.YEAR]: 'vsLastYear',
    [ReportPeriod.CUSTOM]: 'vsPreviousPeriod',
};

const SummaryCards = ({ topStatistics, timeFrame }: ISummaryCardsProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const trendLabelKey = TREND_LABEL_KEYS[timeFrame] ?? 'vsPreviousPeriod';

    const namedStatValue = (
        stat: IClientSourceOfHireTopStatistics['topSource'],
        valueKey: string
    ): string =>
        stat.sourceTitle === null
            ? '-'
            : t(valueKey, {
                  name: stat.sourceTitle,
                  percent: Math.round(stat.totalPercent),
              });

    const cards: {
        icon: string;
        label: string;
        value: string;
        trend?: number | null;
    }[] = [
        {
            icon: 'PeopleOutlineOutlinedIcon',
            label: t('reportsPage.sourceOfHireReport.totalHires'),
            value: t('reportsPage.sourceOfHireReport.employeesHired', {
                count: topStatistics.totalHires,
            }),
            trend: topStatistics.percentChange,
        },
        {
            icon: 'EmojiEventsOutlinedIcon',
            label: t('reportsPage.sourceOfHireReport.topSource'),
            value: namedStatValue(
                topStatistics.topSource,
                'reportsPage.sourceOfHireReport.topSourceValue'
            ),
        },
        {
            icon: 'MailOutlineIcon',
            label: t('reportsPage.sourceOfHireReport.bestConversion'),
            value: namedStatValue(
                topStatistics.bestConversion,
                'reportsPage.sourceOfHireReport.bestConversionValue'
            ),
        },
    ];

    return (
        <StyledCardsRow direction="row">
            {cards.map((card) => {
                const hasTrend = typeof card.trend === 'number';
                const isPositiveTrend = hasTrend && (card.trend as number) >= 0;
                const trendColor = isPositiveTrend
                    ? theme.palette.success.main
                    : theme.palette.error.main;
                return (
                    <StyledCardItem direction="row" key={card.label}>
                        <StyledIconCircle>
                            <Icon
                                name={card.icon}
                                size={24}
                                style={{ color: theme.palette.common.white }}
                            />
                        </StyledIconCircle>
                        <Stack sx={{ minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                color={theme.palette.grey[500]}
                            >
                                {card.label}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {card.value}
                            </Typography>
                            {hasTrend && (
                                <StyledTrendRow direction="row">
                                    <Icon
                                        name={
                                            isPositiveTrend
                                                ? 'MovingIcon'
                                                : 'TrendingDownIcon'
                                        }
                                        size={16}
                                        style={{ color: trendColor }}
                                    />
                                    <Typography
                                        variant="caption"
                                        color={trendColor}
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {Math.abs(card.trend as number)}%
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color={theme.palette.grey[500]}
                                    >
                                        {t(
                                            `reportsPage.sourceOfHireReport.${trendLabelKey}`
                                        )}
                                    </Typography>
                                </StyledTrendRow>
                            )}
                        </Stack>
                    </StyledCardItem>
                );
            })}
        </StyledCardsRow>
    );
};

export default SummaryCards;
